/**
 *
 * browser helper
 *
 */
import Bowser from 'bowser';

import { config } from '../config';

export function getBrowserName() {
  const browser = Bowser.getParser(window.navigator.userAgent);
  const name = browser.getBrowserName();
  return name;
}

export function getFullUserAgent() {
  return navigator.userAgent;
}

/**
 * Check if the current user agent is in an allowable list (from the config).
 * Note: the config list is comma separated
 * @returns boolean
 */

export const isAcceptableUserAgent = () => {
  // Split config into an array of acceptable keywords
  const acceptableAgents = config.REACT_APP_EXAM_ALLOWABLE_USER_AGENTS.split(
    ',',
  ).map(agent => agent.toLowerCase().trim());
  // Check if "ALL" is in the config
  const hasAllInConfig = acceptableAgents.includes('all');
  if (hasAllInConfig) {
    return true;
  }
  // Check if any of the acceptable agents are in the user agent string
  const lowerCaseUserAgent = getFullUserAgent().toLowerCase();
  const matches = acceptableAgents.some(agent =>
    lowerCaseUserAgent.includes(agent),
  );

  // THE SDK does not allow access to the console so we need to do this with an alert
  if (config.REACT_APP_DEBUG_USER_AGENT === 'true' && !matches) {
    // eslint-disable-next-line no-alert
    alert(`User Agent:  ${navigator.userAgent}`);
  }

  return matches;
};

export function getUrlParams(url) {
  const searchParams = new URLSearchParams(new URL(url).search);
  return Object.fromEntries(searchParams.entries());
}

export function searchUsingWindowFind(e, searchRef) {
  const searchValue = e.target.value;

  if (searchValue.length >= 3) {
    searchForText(searchValue, searchRef);
  } else {
    searchForText('', searchRef);
  }
}

export function searchForText(text, searchRef = undefined, color = 'orange') {
  const searchInput = document.getElementById('hid_search');
  if (!searchInput) return;

  if (color !== 'transparent') {
    searchForText(searchInput.value, searchRef, 'transparent');
    searchInput.value = text;
  }

  const outerBox = document.getElementById('outerBox');
  const mainTestSection = document.getElementById('mainTestSection');
  const leftPassage = document.getElementById('leftPassagePage');
  const rightPassage = document.getElementById('rightPassagePage');

  const searchScopes = [
    outerBox,
    mainTestSection,
    leftPassage,
    rightPassage,
  ].filter(Boolean);

  // If there's no search input, clear and exit
  if (text.length < 1) {
    clearPreviousHighlights();
    searchRef?.current?.focus();
    return;
  }

  // Normalize the search query (case and space-insensitive)
  const normalizedSearch = normalizeWhitespace(text.toLowerCase());

  // DOM-based search (fallbacks follow if unsupported)
  if (document.createTreeWalker) {
    clearPreviousHighlights();

    // Collect all valid text nodes using Treewalker
    const textNodes = [];
    let rawText = '';
    let offset = 0;

    searchScopes.forEach(scope => {
      const treeWalker = document.createTreeWalker(
        scope,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: node => {
            const parent = node.parentNode;
            if (
              !parent ||
              ['SCRIPT', 'STYLE', 'NOSCRIPT', 'IFRAME', 'OBJECT'].includes(
                parent.nodeName,
              )
            ) {
              return NodeFilter.FILTER_REJECT;
            }
            if (!node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
          },
        },
      );

      // Flatten text content and track text node boundaries
      while (treeWalker.nextNode()) {
        const node = treeWalker.currentNode;
        const value = node.nodeValue;
        rawText += value;
        textNodes.push({
          node,
          start: offset,
          end: offset + value.length,
        });
        offset += value.length;
      }
    });

    // Normalize whitespace in full text and map positions to original indices
    const map = [];
    let normalizedText = '';
    for (let i = 0; i < rawText.length; i += 1) {
      const char = rawText[i];
      if (/\s/.test(char)) {
        if (normalizedText[normalizedText.length - 1] !== ' ') {
          normalizedText += ' ';
          map.push(i);
        }
      } else {
        normalizedText += char.toLowerCase();
        map.push(i);
      }
    }

    // Find all match positions
    const matches = [];
    let index = 0;
    while (true) {
      index = normalizedText.indexOf(normalizedSearch, index);
      if (index === -1) break;
      const originalStart = map[index];
      const originalEnd = map[index + normalizedSearch.length - 1] + 1;
      matches.push({ start: originalStart, end: originalEnd });
      index += normalizedSearch.length;
    }

    // Highlight all matches in reverse order to avoid DOM disruption
    for (let i = matches.length - 1; i >= 0; i -= 1) {
      const { start, end } = matches[i];
      const range = document.createRange();

      let startNode;
      let startOffset;
      const startMatch = textNodes.find(
        ({ start: s, end: e }) => start >= s && start < e,
      );
      if (startMatch) {
        startNode = startMatch.node;
        startOffset = start - startMatch.start;
      }

      let endNode;
      let endOffset;
      const endMatch = textNodes.find(
        ({ start: s, end: e }) => end > s && end <= e,
      );
      if (endMatch) {
        endNode = endMatch.node;
        endOffset = end - endMatch.start;
      }

      // Highlight the matched range with a span
      if (startNode && endNode) {
        range.setStart(startNode, startOffset);
        range.setEnd(endNode, endOffset);

        const span = document.createElement('span');
        span.setAttribute('data-highlight', 'true');
        span.style.backgroundColor = color;

        const contents = range.extractContents();
        span.appendChild(contents);
        range.insertNode(span);
      }
    }

    searchRef?.current?.focus();
    return;
  }

  // Fallback for old browsers
  if (window.find && window.getSelection) {
    document.designMode = 'on';
    const sel = window.getSelection();
    sel?.collapse(document.body, 0);

    let found = false;
    while (window.find(text)) {
      document.execCommand('HiliteColor', false, color);
      sel?.collapseToEnd();
      found = true;
    }
    document.designMode = 'off';

    if (found) {
      searchRef?.current?.focus();
      return;
    }
  }

  // Fallback: Legacy IE text range highlighting
  if (document.body.createTextRange) {
    const textRange = document.body.createTextRange();
    let found = false;

    while (textRange.findText(text)) {
      textRange.execCommand('BackColor', false, color);
      textRange.collapse(false);
      found = true;
    }

    if (found) {
      searchRef?.current?.focus();
    }
  }
}

function normalizeWhitespace(str) {
  return str.replace(/\s+/g, ' ').trim();
}

function clearPreviousHighlights() {
  const highlights = document.querySelectorAll('span[data-highlight="true"]');
  highlights.forEach(span => {
    const parent = span.parentNode;
    while (span.firstChild) {
      parent.insertBefore(span.firstChild, span);
    }
    parent.removeChild(span);
    parent.normalize();
  });
}

export function isPageReloaded() {
  const pageAccessedByReload =
    (window.performance.navigation &&
      window.performance.navigation.type === 1) ||
    window.performance
      .getEntriesByType('navigation')
      .map(nav => nav.type)
      .includes('reload');

  return pageAccessedByReload;
}

export function searchOptimization(fnc, timeout = 300) {
  // set the focus to input element
  const searchInput = document.getElementById('search-input-field');
  if (searchInput?.focus) {
    searchInput.focus();
  }
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fnc.apply(this, args);
    }, timeout);
  };
}

// Check for speechSynthesis for text-to-speech feature
export function checkTextToSpeech() {
  return 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
}

/**
 * Convert a Blob to a base64 string
 * @param {Blob} blob the blob to convert
 * @returns base64 string of the converted blob
 */
export async function convertBlobToBase64(blob) {
  const base64String = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => {
      reader.abort();
      reject(new Error('Failed to read blob as base64'));
    };
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = dataUrl.split(',')[1];
      resolve(base64);
    };
    reader.readAsDataURL(blob);
  });

  return base64String;
}
